import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  InputAdornment,
  MenuItem,
  Link,
  Divider,
  CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import ChatIcon from "@mui/icons-material/Chat";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

import CountryCodeSelect from "../../common/country-code-select";
import { getIsoPhoneError } from "../../common/phone-validation";
import { useAuth } from "../../hooks/AuthProvider";
import { GET_TENANT_ONBOARDING_OPTIONS_USER_DETAILS } from "../../config/urls";

const UserDetailsForm = ({ userData, errors, onChange, promoCode = null, onPromoBlur }) => {
  const { getValidToken } = useAuth();

  const [onboardingOptions, setOnboardingOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    getOnboardingOptions();
  }, []);

  const getOnboardingOptions = async () => {
    const headers = {
      "Content-Type": "application/json",
    };

    const url = GET_TENANT_ONBOARDING_OPTIONS_USER_DETAILS;

    try {
      const response = await fetch(url, { headers });

      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();

      setOnboardingOptions(data);
    } catch (error) {
      console.error("Error fetching onboarding options: ", error);
    } finally {
      setOptionsLoading(false);
    }
  };

  // Typed number is checked live against the selected country; the Next-step error shows while empty
  const phoneError = userData.phone ? getIsoPhoneError(userData.phone, userData.countryCode) : errors.phone;

  const disallowBothChannelPlans = ["engage", "automate"];
  const shouldHideBothChannel = disallowBothChannelPlans.includes(
    (userData.waPlan || "").toLowerCase(),
  );
  const filteredChannelOptions = shouldHideBothChannel
    ? (onboardingOptions?.channel_options || []).filter(
        (option) => (option.value || "").toLowerCase() !== "both",
      )
    : onboardingOptions?.channel_options || [];

  if (optionsLoading) {
    return (
      <Box
        sx={{
          w: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Profile Setup
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Please provide your personal information to create your account.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            value={userData.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            value={userData.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="email"
            label="Work Email"
            name="email"
            type="email"
            value={userData.email}
            onChange={(e) => onChange("email", e.target.value)}
            error={!!errors.email}
            helperText={
              errors.emailAction === "login" ? (
                <span>
                  Looks like you are already a BotBridge user. Please{" "}
                  <Link href="/login" target="_blank" rel="noopener">
                    login
                  </Link>
                </span>
              ) : (
                errors.email
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CountryCodeSelect
            value={userData.countryCode}
            onChange={(iso) => onChange("countryCode", iso)}
            label="Country"
            required
            sx={{ width: 220, flexShrink: 0 }}
            TextFieldProps={{ id: "countryCode", error: !!errors.countryCode, helperText: errors.countryCode }}
          />

          <TextField
            required
            fullWidth
            id="phone"
            label="Contact Phone Number"
            name="phone"
            value={userData.phone}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/[^0-9]/g, "");
              onChange("phone", onlyNums);
            }}
            // 🟢 FIX: Removed manual maxLength. The library handles the validation now.
            error={!!phoneError}
            helperText={phoneError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <TextField
            required
            fullWidth
            select
            id="role"
            label="Your Role"
            name="role"
            value={userData.role}
            onChange={(e) => onChange("role", e.target.value)}
            error={!!errors.role}
            helperText={errors.role}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WorkIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {onboardingOptions?.role_options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 🟢 CHANGED: Set to sm={6} to share the row */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="waPlan"
            label="Select WhatsApp Plan"
            name="waPlan"
            value={userData.waPlan}
            onChange={(e) => onChange("waPlan", e.target.value)}
            error={!!errors.waPlan}
            helperText={errors.waPlan}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ChatIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {onboardingOptions?.plan_options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 🟢 NEW: Channel Dropdown */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="channel"
            label="Select Channel"
            name="channel"
            value={
              shouldHideBothChannel &&
              (userData.channel || "").toLowerCase() === "both"
                ? ""
                : userData.channel || ""
            }
            onChange={(e) => onChange("channel", e.target.value)}
            error={!!errors.channel}
            helperText={errors.channel}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ChatIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {filteredChannelOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* {onboardingOptions?.show_promo_code && (*/}
        {true && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="promoCode"
              label="Promo Code"
              name="promoCode"
              value={userData.promoCode || ""}
              onChange={(e) => onChange("promoCode", e.target.value)}
              onBlur={onPromoBlur}
              error={!!errors.promoCode}
              helperText={errors.promoCode}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOfferIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UserDetailsForm;
